"use client";

import { cn } from "@/lib/utils";
import { normalizeCmsHtml } from "@/lib/cmsHtml";
import React, { memo, useMemo } from "react";
import RevealOnScroll from "@/components/atoms/RevealOnScroll";

interface FooterLink {
  id: number;
  label: string;
  label_color?: string;
  url: string;
  slug?: string;
  description?: string;
  priority: number;
}

interface FooterCategory {
  id: number;
  name: string;
  name_text_color?: string;
  order_index: number;
  status: boolean;
  items?: FooterLink[];
}

interface FooterData {
  background_color?: string;
  text_color?: string;
  copyright_text?: string;
  copyright_text_color?: string;
  enable_copyright?: boolean;
  enable_footer_content?: boolean;
  enable_animation?: boolean | number | string;
  categories?: FooterCategory[];
}

const Footer1: React.FC<{ data: any }> = ({ data }) => {
  const { data: sectionData } = data;

  if (!sectionData || !sectionData?.status) return null;

  const {
    background_color = "#BE2330",
    text_color = "#FFFFFF",
    copyright_text = "© Copyright 2025 | All Rights Reserved",
    copyright_text_color = "#FFFFFF",
    enable_copyright = true,
    enable_footer_content = true,
    enable_animation = false,
    categories = [],
  } = sectionData || {};

  const isAnimationEnabled =
    enable_animation === true ||
    enable_animation === 1 ||
    enable_animation === "1";

  // Sort categories by order_index and filter by status
  const activeCategories = useMemo(() => {
    if (!categories || categories.length === 0) return [];
    return [...categories]
      .filter((cat: FooterCategory) => cat.status)
      .sort(
        (a: FooterCategory, b: FooterCategory) => a.order_index - b.order_index
      );
  }, [categories]);

  // Parse text with {bold} syntax into React nodes
  const setFontWeight = useMemo(
    () =>
      (text: string): React.ReactNode[] => {
        const regex = /{([^}]+)}/g;
        const parts: React.ReactNode[] = [];
        let lastIndex = 0;
        let match;

        while ((match = regex.exec(text)) !== null) {
          if (match.index > lastIndex) {
            parts.push(text.slice(lastIndex, match.index));
          }

          parts.push(
            <strong key={match.index} className="font-semibold">
              {match[1]}
            </strong>
          );

          lastIndex = regex.lastIndex;
        }

        if (lastIndex < text.length) {
          parts.push(text.slice(lastIndex));
        }

        return parts;
      },
    []
  );

  return (
    <footer className="w-full" style={{ color: text_color }}>
      <div
        className={cn(
          "w-full flex items-center justify-center mx-auto sm:px-9 sm:py-[42px]",
          "xs:py-8 xs:px-5"
        )}
        style={{ backgroundColor: background_color }}
      >
        <div className="sm:w-screen flex flex-col flex-wrap items-center justify-center">
          {enable_footer_content && activeCategories.length > 0 && (
            <div
              className={cn(
                "grid sm:px-0 px-0 sm:pb-10 pb-8 border-b border-gray-400",
                "xs:gap-[68px] xs:grid-cols-1 xs:flex-col",
                "sm:gap-x-3 sm:gap-y-6 sm:flex-row sm:grid-cols-4 ",
                "md:gap-x-6 md:gap-y-[30px]",
                "lg:gap-x-16"
              )}
            >
              {activeCategories.map((category: FooterCategory) => {
                // Sort items by priority
                const sortedLinks = category.items
                  ? [...category.items].sort(
                      (a: FooterLink, b: FooterLink) => a.priority - b.priority
                    )
                  : [];

                return (
                  <div
                    key={category.id}
                    className={cn(
                      "sm:block sm:w-[224px]",
                      "md:w-[260px]",
                      "lg:w-[260px]",
                      "xl:w-[360px]"
                    )}
                  >
                    <div
                      className={cn(
                        "flex flex-col items-center",
                        "xs:text-heading-8-bold",
                        "sm:block sm:w-[224px]",
                        "md:w-[260px]",
                        "lg:w-[260px]",
                        "xl:w-[360px]"
                      )}
                    >

                      <RevealOnScroll enableAnimation={isAnimationEnabled}>

                      <h3
                        className={cn(
                          "leading-normal w-fit",
                          "xs:mb-3",
                          "sm:text-heading-9-bold sm:border-b sm:border-gray-400 sm:mb-3",
                          "md:text-heading-8-bold",
                          "lg:text-heading-7-bold",
                          "xl:text-new-body-24-bold"
                        )}
                        style={{
                          color: category.name_text_color || text_color,
                        }}
                      >
                        {category.name}
                      </h3>
                      </RevealOnScroll>

                      <ul
                        className={cn(
                          "flex flex-col",
                          "xs:gap-[18px]",
                          "sm:pr-2 sm:gap-[10px] sm:max-w-[240px]"
                        )}
                      >
                        {sortedLinks.map((link: FooterLink) => (
                          <li
                            key={link.id}
                            className="flex sm:items-start sm:justify-start sm:wrap-break-word items-center justify-center"
                          >
                            <a
                              href={link.url}
                              data-cta-name="footer_link"
                              className={cn(
                                "hover:opacity-80 transition-opacity sm:text-left text-center font-normal leading-normal",
                                "xs:text-heading-9-regular",
                                "sm:text-heading-9-regular",
                                "md:text-heading-9-regular"
                              )}
                              style={{ color: link.label_color || text_color }}
                            >
                              {setFontWeight(link.label)}
                            </a>
                          </li>
                        ))}
                      </ul>

                      {/* Render description if available */}
                      {sortedLinks.some((link) => link.description) && (
                        <div
                          className={cn("mt-4", {
                            "flex flex-wrap gap-2":
                              category.name === "Follow Us",
                          })}
                        >
                          {sortedLinks
                            .filter((link) => link.description)
                            .map((link) => (
                              <div
                                key={`desc-${link.id}`}
                                className={cn(
                                  "w-fit h-fit",
                                  "xs:text-center",
                                  "sm:text-left"
                                )}
                                dangerouslySetInnerHTML={{
                                  __html: normalizeCmsHtml(
                                    link.description || "",
                                  ),
                                }}
                                style={{
                                  color: link.label_color || text_color,
                                }}
                              />
                            ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {enable_copyright && copyright_text && (
            <div
              className={cn(
                "xs:text-heading-8-bold",
                `sm:text-base text-xs font-normal leading-normal text-center `,
                {
                  "sm:pt-4 pt-8": activeCategories.length > 0,
                },
                "md:pt-[18px]"
              )}
              style={{ color: copyright_text_color }}
              dangerouslySetInnerHTML={{
                __html: normalizeCmsHtml(copyright_text),
              }}
            />
          )}
        </div>
      </div>
    </footer>
  );
};

export default memo(Footer1);
